import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';

// Initialize Stripe
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey || stripePublishableKey === 'REPLACE_WITH_YOUR_ACTUAL_STRIPE_KEY') {
  console.warn('⚠️  REACT_APP_STRIPE_PUBLISHABLE_KEY is not properly set in environment variables');
  console.warn('⚠️  Stripe payments will not work until you add a valid key to .env file');
}

let stripePromise: Promise<Stripe | null>;

const getStripe = () => {
  if (!stripePromise) {
    // Only load Stripe if we have a valid key
    if (stripePublishableKey && stripePublishableKey !== 'REPLACE_WITH_YOUR_ACTUAL_STRIPE_KEY') {
      stripePromise = loadStripe(stripePublishableKey);
    } else {
      stripePromise = Promise.resolve(null);
    }
  }
  return stripePromise;
};

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  priceId: string;
  features: string[];
}

export const subscriptionPlans: SubscriptionPlan[] = [
  {
    id: 'monthly',
    name: 'Premium Monthly',
    price: 18.99,
    interval: 'month',
    priceId: 'price_1NjT8IBH0i49Oiov7d6px8Xu',
    features: [
      'Unlimited goals and tracking',
      'Advanced talent tree system',
      'Detailed analytics and insights',
      'Priority support',
      'Export your data'
    ]
  },
  {
    id: 'annual',
    name: 'Premium Annual',
    price: 185.00,
    interval: 'year',
    priceId: 'price_1NjTLMBH0i49OiovHoU0R9OU',
    features: [
      'All Premium Monthly features',
      'Save $42.88 per year',
      'Exclusive annual subscriber perks',
      'Priority feature requests'
    ]
  }
];

export interface CreateSubscriptionParams {
  priceId: string;
  userId: string;
  userEmail: string;
  userName: string;
}

export const testEdgeFunction = async () => {
  console.log('🧪 Testing Edge Function connectivity...');
  
  try {
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        priceId: 'test',
        userId: 'test',
        userEmail: 'test@example.com',
        userName: 'Test User'
      }
    });

    console.log('🧪 Test response:', { data, error });
    
    if (error) {
      console.log('❌ Edge Function error (expected for test):', error);
    }
    
    return { data, error };
  } catch (error) {
    console.error('❌ Test failed:', error);
    throw error;
  }
};

export const createSubscription = async (params: CreateSubscriptionParams) => {
  console.log('🔄 createSubscription called with params:', params);
  
  try {
    console.log('📡 Calling Supabase Edge Function: create-checkout-session');
    
    // Get the Supabase project URL and API key for Edge Functions
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: params
    });

    console.log('📨 Edge Function response:', { data, error });

    if (error) {
      console.error('❌ Edge Function error:', error);
      console.error('❌ Full error object:', JSON.stringify(error, null, 2));
      throw new Error(`Failed to create checkout session: ${error.message || 'Edge Function returned a non-2xx status code'}`);
    }

    if (!data) {
      console.error('❌ No data in response');
      throw new Error('No data returned from checkout session');
    }

    if (!data.sessionUrl) {
      console.error('❌ No session URL in response:', data);
      // Check if there's an error message in the data
      if (data.error) {
        throw new Error(`Checkout session error: ${data.error}`);
      }
      throw new Error('No session URL returned from checkout session');
    }
    
    console.log('✅ Redirecting to Stripe Checkout:', data.sessionUrl);
    // Redirect to Stripe Checkout
    window.location.href = data.sessionUrl;
  } catch (error: any) {
    console.error('❌ Error creating subscription:', error);
    console.error('❌ Error stack:', error.stack);
    throw error;
  }
};

export const createCustomerPortalSession = async (userId: string) => {
  try {
    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: { userId }
    });

    if (error) {
      throw new Error(`Failed to create portal session: ${error.message}`);
    }

    if (!data?.sessionUrl) {
      throw new Error('No session URL returned from portal session');
    }

    window.location.href = data.sessionUrl;
  } catch (error) {
    console.error('Error creating portal session:', error);
    throw error;
  }
};

export const getUserSubscription = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('stripe_customer_id, stripe_subscription_id, subscription_status, subscription_plan, subscription_tier, subscription_expires_at, cancel_at_period_end, subscription_period_end_at')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    throw error;
  }
};

export const updateUserSubscription = async (
  userId: string, 
  subscriptionData: {
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    subscription_status?: string;
    subscription_plan?: string;
    subscription_tier?: string;
    subscription_expires_at?: string;
    cancel_at_period_end?: boolean;
    subscription_period_end_at?: string;
  }
) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update(subscriptionData)
      .eq('id', userId)
      .select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user subscription:', error);
    throw error;
  }
};

export const hasActiveSubscription = (subscriptionStatus?: string): boolean => {
  return subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
};

export const isPremiumUser = (subscriptionTier?: string, subscriptionStatus?: string, expiresAt?: string): boolean => {
  // Check if user has premium tier
  if (subscriptionTier !== 'premium') return false;
  
  // If subscription is active or trialing, they have premium access
  if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
    return true;
  }
  
  // For canceled subscriptions, immediately revoke access (no grace period)
  if (subscriptionStatus === 'canceled') {
    return false;
  }
  
  return false;
};

export const getSubscriptionStatusMessage = (subscriptionStatus?: string, expiresAt?: string): string => {
  if (!subscriptionStatus || subscriptionStatus === 'free') {
    return 'Free Plan';
  }
  
  switch (subscriptionStatus) {
    case 'active':
      return 'Active Subscription';
    case 'trialing':
      return 'Trial Period';
    case 'canceled':
      return 'Subscription Canceled';
    case 'past_due':
      return 'Payment Past Due';
    case 'incomplete':
      return 'Payment Incomplete';
    default:
      return subscriptionStatus.charAt(0).toUpperCase() + subscriptionStatus.slice(1);
  }
};

export const createClientSideCheckout = async (params: CreateSubscriptionParams) => {
  console.log('🔄 Creating client-side Stripe checkout...');
  
  const successUrl = `${window.location.origin}/subscription/success`;
  const cancelUrl = `${window.location.origin}/pricing?canceled=true`;
  
  console.log('🔍 Stripe URLs Debug:', {
    windowOrigin: window.location.origin,
    successUrl,
    cancelUrl,
    currentUrl: window.location.href
  });
  
  try {
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe is not properly configured. Please add a valid REACT_APP_STRIPE_PUBLISHABLE_KEY to your .env file.');
    }

    console.log('🔄 Redirecting to Stripe Checkout (client-side)...');
    
    // For now, redirect directly to a Stripe payment link or use Stripe's client-side checkout
    // This is a temporary solution while we fix the Edge Function
    const { error } = await stripe.redirectToCheckout({
      lineItems: [
        {
          price: params.priceId,
          quantity: 1,
        },
      ],
      mode: 'subscription',
      successUrl,
      cancelUrl,
      customerEmail: params.userEmail,
    });

    if (error) {
      console.error('❌ Stripe checkout error:', error);
      throw new Error(error.message || 'Stripe checkout failed');
    }
  } catch (error) {
    console.error('❌ Client-side checkout error:', error);
    throw error;
  }
};

export { getStripe }; 